import { darken, lighten, transparentize } from 'polished'

import { themeHelpers } from '@subaio/utils'
import { defaultButtonTheme, defaultInputTheme, defaultSpacing } from '@subaio/webapp'

const animation = {
	default: 'cubic-bezier(0.4, 0, 0.2, 1)',
}

const theme = () => {
	const colors = {
		subaioYellow: '#fff07c',
		subaioDarkGrey: '#4b4847',
		subaioLightGrey: '#ddd8cd',
		subaioGreen: '#304f44',
		subaioGreenDark: '#16342A',
		subaioGreenLight: '#37c392',
		subaioOlive: '#a38f2d',
		subaioLime: '#D7F300',

		alert: '#E70404',
		warning: '#FFCF3D',
		text: '#000',
		navBorders: '#C9C7C7',

		black: '#000',
		white: '#FFF',
	}

	const font = {
		family: {
			header: `'DM Sans', sans-serif`,
			base: `'DM Sans', sans-serif`,
			code: `'DM Sans', monospace`,
		},
		size: {
			largest: '28px',
			larger: '22px',
			large: '18px',
			base: '16px',
			small: '14px',
			smaller: '12px',

			h1: '24px',
			h2: '18px',
			h3: '14px',
		},
	}

	return {
		color: {
			white: colors.white,
			black: colors.black,
			cardBackground: colors.white,
			cardShadow: transparentize(0.88, colors.black),
			cardSubsectionBackground: '#f5f5f5',
			contextCardBackground: 'rgba(236, 241, 213, 0.88)',

			actionLabel: colors.white,
			action: colors.subaioGreen,
			actionBorder: transparentize(0.88, colors.black),
			actionHover: darken(0.1, colors.subaioGreen),
			actionDisabled: '#83B8ED',
			actionDisabledLabel: colors.subaioDarkGrey,
			actionLight: '#DCEDFF',
			link: '#3f3fff',
			backgroundSplash: '#F1F2F4',
			pageHeader: colors.subaioGreen,

			iconBackground: '#E3E3E3',
			iconBackgroundHighlighted: '#00005E',

			headerIconPrimary: '#0000A0',
			headerIconPrimaryAccent: '#83B8ED',
			headerIconBackground: colors.white,
			headerIconGradientStart: colors.white,
			headerIconGradientStop: transparentize(0.8, colors.subaioGreen),

			warning: colors.warning,

			text: colors.text,
			textMuted: '#5A575C',
			textContrast: colors.white,
			border: colors.navBorders,
			background: colors.white,
			separator: '#eae9e9',
			scrollbarThumb: '#ADB1B2',

			disabled: '#808080',
			disabledContrast: '#E0E0E0',

			inputFocus: '#0000A0',
			inputBorder: '#C9C7C7',
			inputDisabled: '#F1F2F4',
			inputIcon: '#00005E',
			inputIconBackground: '#F1F2F4',

			input: {
				...defaultInputTheme,
				label: colors.text,

				focusOutline: colors.subaioGreen,

				background: colors.white,
				backgroundHover: colors.white,
				backgroundReadOnly: colors.white,
				backgroundError: 'hsl(0, 80%, 95%)',
				backgroundErrorHover: 'hsl(0, 80%, 95%)',

				border: '#C9C7C7',
				borderHover: colors.subaioGreen,
				borderError: colors.alert,
				borderOutlineHover: colors.subaioGreen,
				borderOutlineError: colors.alert,

				menuIndicator: transparentize(0.88, colors.black),
				menuIndicatorActive: colors.black,
				menuOptionCheckMark: colors.subaioGreen,

				menuOptionBackgroundHighlighted: transparentize(0.8, colors.subaioGreen),
				menuOptionBackgroundSelected: transparentize(0.9, colors.subaioGreen),
				menuOptionBackgroundHover: transparentize(0.8, colors.subaioGreen),
			},

			// Required because of the theme set in @subaio/components
			primary: colors.subaioLime,
			primaryLight: lighten(0.22, colors.subaioLime),
			secondary: colors.subaioGreenDark,
			danger: colors.alert,
			textHeader: colors.text,

			// Required because of the theme set in @subaio/webapp
			/** System status indicators */
			status: {
				operational: '#5EB870',
				minor: '#FCB900',
				major: '#F27641',
				critical: '#E15554',
			},

			button: defaultButtonTheme,
		},
		animation,
		font,
		spacing: defaultSpacing,
		borderRadius: '4px',
		colorScheme: 'light',
	}
}

export const getTheme = () => ({
	...theme(),
})

export const helpers = themeHelpers(getTheme())

export type Theme = ReturnType<typeof getTheme>
